import Script from 'next/script';

import NormalRoute from '@/components/NormalRoute';
import PrivateRoute from '@/components/PrivateRoute';
import PublicRoute from '@/components/PublicRoute';
import '@/styles/globals.css';
import DefaultTheme from '@/themes';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { NextPage } from 'next';
import { appWithTranslation, UserConfig } from 'next-i18next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { Next13ProgressBar } from 'next13-progressbar';
import { ReactElement, ReactNode, lazy, useState } from 'react';
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import nextI18NextConfig from '../../next-i18next.config';
import 'moment-timezone';
import moment from 'moment-timezone';

const clientSideEmotionCache = () => createCache({ key: 'css', prepend: true });

const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID

type NextPageCustom = NextPage & {
  getLayout?: (page: ReactElement, _props?: any) => ReactNode;
  type?: 'private' | 'public';
};

type AppPropsCustom = AppProps & {
  Component: NextPageCustom;
};

const ReactQueryDevtoolsDevelopment = lazy(() =>
  import('react-query/devtools/development').then(d => ({
    default: d.ReactQueryDevtools,
  })),
);

const currentTime = moment().format('YYYY-MM-DD HH:mm:ss Z');

if (!currentTime.includes('+07:00')) {
  moment.tz.setDefault('Asia/Ho_Chi_Minh');
  console.log('moment.tz.setDefault:', moment().format('YYYY-MM-DD HH:mm:ss Z'));
}


function App({
  Component,
  pageProps: { session, ...pageProps },
}: AppPropsCustom) {
  const getLayout = Component.getLayout ?? ((page: ReactElement, _props?: any) => page);

  const [queryClient] = useState(
    new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          retryOnMount: false,
          refetchOnWindowFocus: false,
        },
      },
    }),
  );

  return (
    <>
      <Head>
        <title>Hoi An GO - E-Taxi & Tour Booking</title>
        <link
          rel="icon"
          href='/fav/favicon.ico'
        ></link>
        <meta name="theme-color" content="#012F5B" />
      </Head>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <CacheProvider value={clientSideEmotionCache()}>
            <ThemeProvider theme={DefaultTheme}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                {Component.type === 'private' && (
                  <PrivateRoute>
                    {getLayout(<Component {...pageProps} />, { ...pageProps })}
                  </PrivateRoute>
                )}
                {Component.type === 'public' && (
                  <PublicRoute>
                    {getLayout(<Component {...pageProps} />, { ...pageProps })}
                  </PublicRoute>
                )}
                {!Component.type && (
                  <NormalRoute>
                    {getLayout(<Component {...pageProps} />, { ...pageProps })}
                  </NormalRoute>
                )}
                <Next13ProgressBar
                  height="4px"
                  color={DefaultTheme.palette.primary.main}
                  options={{ showSpinner: false }}
                />
              </LocalizationProvider>
            </ThemeProvider>
          </CacheProvider>
        </Hydrate>

        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtoolsDevelopment
            initialIsOpen={false}
            position="bottom-right"
          />
        )}
      </QueryClientProvider>

      {(process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && !window.location.hostname.includes('k2ktech')) && (
        <>
          {/*<Script id="facebook-pixel">
            {`
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${FB_PIXEL_ID}');
        fbq('track', 'PageView');
      `}
          </Script>*/}

          <noscript>
            <img
              height="1"
              width="1"
              style={{ "display": "none" }}
              alt="facebook-pixel"
              src="https://www.facebook.com/tr?id=553651713939731&ev=PageView&noscript=1"
            />
          </noscript>

          {/*<!-- Google tag (gtag.js) -->*/}
          <Script id="google-tag" async src="https://www.googletagmanager.com/gtag/js?id=G-F021VE1430">
          </Script>
          <Script id="google-tag">
            {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-F021VE1430');
            `}
          </Script>
        </>
      )}
    </>
  );
}

export default appWithTranslation(App, nextI18NextConfig as UserConfig);
